import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useOrganization } from "@/hooks/useOrganization";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { User, Mail, Crown, Lock, Eye, EyeOff } from "lucide-react";
import { z } from "zod";

const passwordSchema = z.string().min(8, 'A senha deve ter pelo menos 8 caracteres').regex(/[A-Z]/, 'A senha deve conter pelo menos uma letra maiúscula').regex(/[a-z]/, 'A senha deve conter pelo menos uma letra minúscula').regex(/[0-9]/, 'A senha deve conter pelo menos um número').regex(/[^A-Za-z0-9]/, 'A senha deve conter pelo menos um caractere especial');

interface UserProfileDialogProps {
  children: React.ReactNode;
}

export const UserProfileDialog: React.FC<UserProfileDialogProps> = ({
  children
}) => {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  
  // Owner email fetched from edge function
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);
  const [loadingOwnerEmail, setLoadingOwnerEmail] = useState(false);

  // Fetch owner email when dialog opens
  useEffect(() => {
    const fetchOwnerEmail = async () => {
      if (!isOpen || !user) return;
      
      setLoadingOwnerEmail(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-owner-email');
        
        if (error) {
          console.error('Error fetching owner email:', error);
          setOwnerEmail(null);
        } else if (data?.owner_email) {
          setOwnerEmail(data.owner_email);
        }
      } catch (err) {
        console.error('Error invoking get-owner-email:', err);
        setOwnerEmail(null);
      } finally {
        setLoadingOwnerEmail(false);
      }
    };

    fetchOwnerEmail();
  }, [isOpen, user]);

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handlePasswordChange = async () => {
    // Validate password with Zod schema
    try {
      passwordSchema.parse(newPassword);
    } catch (validationError) {
      if (validationError instanceof z.ZodError) {
        toast({
          variant: "destructive",
          title: "Senha inválida",
          description: validationError.errors[0].message
        });
        return;
      }
    }
    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Senhas não coincidem",
        description: "As senhas digitadas não são iguais."
      });
      return;
    }
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      toast({
        title: "Senha alterada",
        description: "Sua senha foi alterada com sucesso."
      });
      setNewPassword("");
      setConfirmPassword("");
      setIsChangingPassword(false);
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error);
      toast({
        variant: "destructive",
        title: "Erro ao alterar senha",
        description: error.message || "Não foi possível alterar a senha. Tente novamente."
      });
    }
  };

  const getUserPlan = () => {
    return {
      name: "Radar Financeiro Basic",
      type: "free",
      features: ["Acesso mensal ao aplicativo", "Controle financeiro", "Relatórios gerenciais"]
    };
  };

  const plan = getUserPlan();

  return <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Perfil da Conta
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações da Conta */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Informações da Conta
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Organização</Label>
                <p className="text-sm font-medium">{organization?.name || 'Não disponível'}</p>
              </div>
              
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Seu Email</Label>
                <p className="text-sm font-medium break-words overflow-wrap-anywhere">{user?.email}</p>
              </div>
              
            </CardContent>
          </Card>

          {/* Alterar Senha */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Segurança
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isChangingPassword ? <Button variant="outline" onClick={() => setIsChangingPassword(true)} className="w-full">
                  Alterar Senha
                </Button> : <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Nova Senha</Label>
                    <div className="relative">
                      <Input id="newPassword" type={showPassword ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Digite sua nova senha" className="pr-10" />
                      <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                    <div className="relative">
                      <Input id="confirmPassword" type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirme sua nova senha" className="pr-10" />
                      <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handlePasswordChange} disabled={!newPassword || !confirmPassword} className="flex-1">
                      Salvar Nova Senha
                    </Button>
                    <Button variant="outline" onClick={() => {
                  setIsChangingPassword(false);
                  setNewPassword("");
                  setConfirmPassword("");
                }}>
                      Cancelar
                    </Button>
                  </div>
                </div>}
            </CardContent>
          </Card>

          {/* Plano Atual */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Crown className="h-4 w-4" />
                Plano Atual
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    Solução completa para pequenos e médios negócios
                  </p>
                </div>
                <Badge variant="secondary">
                  Ativo
                </Badge>
              </div>

              <Separator />

              <div>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {plan.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>

            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>;
};